<template>
  <div
    ref="root"
    class="follow-menu"
    role="menu"
    :aria-label="heading"
    :aria-busy="saving ? 'true' : 'false'"
    data-testid="follow-menu"
    @keydown="onKeydown"
  >
    <div class="follow-menu__heading" role="presentation">{{ heading }}</div>

    <button
      v-for="option in CADENCE_OPTIONS"
      :key="option.value"
      type="button"
      role="menuitemradio"
      class="follow-menu__option"
      :class="{ 'follow-menu__option--selected': selected === option.value }"
      :aria-checked="selected === option.value ? 'true' : 'false'"
      :data-testid="`follow-menu-${option.value}`"
      @click="selectCadence(option.value)"
    >
      <span class="follow-menu__text">
        <span class="follow-menu__label">{{ option.label }}</span>
        <span class="follow-menu__description">{{ option.description }}</span>
      </span>
      <v-icon v-if="selected === option.value" class="follow-menu__check" size="20">mdi-check</v-icon>
    </button>

    <div class="follow-menu__divider" role="separator"></div>

    <button
      type="button"
      role="menuitem"
      class="follow-menu__option follow-menu__option--unfollow"
      data-testid="follow-menu-unfollow"
      @click="onUnfollow"
    >
      <span class="follow-menu__label">Unfollow</span>
    </button>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { setFollowCadence } from "@/api";

const CADENCE_OPTIONS = [
  { value: "daily", label: "Daily digest", description: "One email + feed item per day" },
  { value: "immediately", label: "Immediately", description: "Every change and comment as it happens" },
];

const props = defineProps({
  /** The saved cadence: "daily" (default) or "immediately". */
  cadence: {
    type: String,
    default: "daily",
  },
  /** Name of the followed object, shown in the heading. */
  name: {
    type: String,
    default: "",
  },
  /** Move focus to the selected option when the menu mounts (keyboard users). */
  autofocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["cadence-change", "unfollow", "close"]);

const proxy = getCurrentInstance()?.proxy;
const root = ref(null);
const selected = ref(props.cadence);
const saving = ref(false);

const heading = computed(() => (props.name ? `Notify me about ${props.name}` : "Notify me"));

watch(
  () => props.cadence,
  (value) => {
    selected.value = value;
  }
);

onMounted(() => {
  if (props.autofocus) root.value?.querySelector('[aria-checked="true"]')?.focus();
});

async function selectCadence(value) {
  if (saving.value) return;
  if (value === selected.value) {
    emit("close");
    return;
  }
  const previous = selected.value;
  selected.value = value;
  saving.value = true;
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    await setFollowCadence(accountId, value);
    emit("cadence-change", value);
    emit("close");
  } catch (error) {
    selected.value = previous;
    console.error("Failed to save notification cadence", error);
    proxy?.$notifications?.error("Failed to update notification settings.");
  } finally {
    saving.value = false;
  }
}

function onUnfollow() {
  if (!saving.value) emit("unfollow");
}

function onKeydown(event) {
  const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
  if (!step) return;
  event.preventDefault();
  const items = Array.from(root.value.querySelectorAll("button"));
  if (!items.length) return;
  const index = items.indexOf(document.activeElement);
  items[(index + step + items.length) % items.length].focus();
}
</script>

<style scoped>
.follow-menu {
  display: flex;
  flex-direction: column;
  width: 280px;
  padding: 6px 7px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px #10182814, 0px 4px 6px -2px #10182808;
}

.follow-menu__heading {
  padding: 6px 7px;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
}

.follow-menu__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 3px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.follow-menu__option:hover,
.follow-menu__option--selected {
  background: #f9fafb;
}

.follow-menu__option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.follow-menu[aria-busy="true"] .follow-menu__option {
  cursor: progress;
}

.follow-menu__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.follow-menu__label {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}

.follow-menu__description {
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.follow-menu__check {
  flex-shrink: 0;
  color: #6941c6;
}

.follow-menu__divider {
  height: 1px;
  margin: 4px 0;
  background: #eaecf0;
}
</style>
